<template>
  <section class="gt-page loyalty-page">
    <div class="gt-page-inner">
      <div class="hero gt-card">
        <div>
          <span class="gt-pill">Lealtad</span>
          <h1>Wallets, puntos y sitios externos</h1>
          <p class="gt-muted">Configura cada sitio conectado con tokens independientes, sin depender de .env.</p>
        </div>
        <div class="hero-actions">
          <button class="gt-button soft" @click="$router.push('/admin/lealtad/canjes')">Ver reporte de canjes</button>
          <button class="gt-button primary" @click="loadAll">Actualizar</button>
        </div>
      </div>

      <div v-if="message" class="alert" :class="messageType">{{ message }}</div>

      <div class="gt-grid cols-3 cards">
        <article class="gt-card stat">
          <span>Acumulación</span>
          <strong>$10 = 1 punto</strong>
          <small>Basado en {{ settings.earn_amount_cents_per_point || 1000 }} centavos por punto</small>
        </article>
        <article class="gt-card stat">
          <span>Canje</span>
          <strong>1 punto = $1</strong>
          <small>{{ settings.redeem_points_per_mxn || 1 }} punto(s) por peso</small>
        </article>
        <article class="gt-card stat">
          <span>QR de canje</span>
          <strong>{{ settings.redemption_qr_expiration_minutes || 30 }} min</strong>
          <small>Ventana sugerida de validez</small>
        </article>
      </div>

      <section class="automation-card gt-card">
        <div>
          <span class="gt-pill">Entrega garantizada</span>
          <h2>Reintento programado de puntos</h2>
          <p class="gt-muted">El cierre de venta sincroniza de inmediato. Este token sirve únicamente para que n8n o cPanel rescaten eventos que fallaron por red o mantenimiento.</p>
        </div>
        <button class="gt-button soft" :disabled="automationLoading" @click="createAutomationToken">{{ automationLoading ? 'Generando…' : 'Generar token para cron' }}</button>
        <div v-if="automationToken" class="automation-secret">
          <small>Guárdalo ahora; la base conserva solamente su hash.</small>
          <code>{{ automationToken }}</code>
          <div>
            <button class="gt-button soft" @click="copyAutomationToken">Copiar token</button>
            <button class="gt-button primary" @click="copyAutomationCommand">Copiar comando cPanel</button>
          </div>
          <small>Endpoint: {{ automationEndpoint }}</small>
        </div>
      </section>

      <div class="panel-grid">
        <section class="gt-card form-card">
          <div class="section-title-row">
            <div>
              <h2>{{ siteForm.id ? 'Editar sitio externo' : 'Nuevo sitio externo' }}</h2>
              <p class="gt-muted">Cada conexión tiene dos tokens: uno para que el sitio hable con Gestión Total y otro para que Gestión Total sincronice de regreso.</p>
            </div>
            <button v-if="siteForm.id" class="gt-button soft" @click="resetForm">Nuevo</button>
          </div>

          <div class="map-box">
            <strong>Mapa correcto de tokens</strong>
            <span><b>Token de Gestión Total:</b> se genera aquí y se pega en Afrogato como “Token de Gestión Total”.</span>
            <span><b>Token para sincronizar hacia el sitio:</b> se genera en Afrogato y se pega aquí como “Token para sincronizar hacia el sitio”.</span>
          </div>

          <div class="form-grid">
            <label>
              <span>Nombre</span>
              <input v-model="siteForm.name" placeholder="Afrogato App" />
            </label>
            <label>
              <span>Código técnico</span>
              <input v-model="siteForm.code" placeholder="afrogato_app" />
            </label>
            <label>
              <span>Dominio</span>
              <input v-model="siteForm.domain" placeholder="afrogato.app" />
            </label>
            <label>
              <span>API base URL del sitio</span>
              <input v-model="siteForm.api_base_url" placeholder="https://afrogato.app" />
            </label>
            <label>
              <span>URL base para reclamar tickets</span>
              <input v-model="siteForm.ticket_claim_base_url_override" placeholder="Automática desde la API base" />
              <small class="field-hint">Déjala vacía para usar automáticamente “API base URL + /puntos/ticket”. URL efectiva: {{ siteForm.ticket_claim_base_url || 'se calculará al guardar' }}</small>
            </label>
          </div>

          <div class="token-section inbound-token">
            <div class="token-copy">
              <p>Token de Gestión Total para este sitio</p>
              <small>Afrogato debe usar este token para validar tickets, registrar wallets y emitir canjes contra Gestión Total.</small>
              <small>Actual: <b>{{ siteForm.token_preview || 'sin configurar' }}</b></small>
            </div>
            <label>
              <span>Fijar token manualmente</span>
              <input v-model="siteForm.inbound_token" type="password" placeholder="Opcional: pegar token manual" autocomplete="off" />
              <small class="field-hint">Normalmente usa “Generar/rotar token” y copia el token generado a Afrogato.</small>
            </label>
            <label class="switch-row">
              <input v-model="siteForm.generate_inbound_token" type="checkbox" />
              <span>{{ siteForm.id ? 'Generar/rotar token de Gestión Total al guardar' : 'Generar token de Gestión Total al crear' }}</span>
            </label>
          </div>

          <div class="token-section outbound-token">
            <div class="token-copy">
              <p>Token para sincronizar hacia el sitio</p>
              <small>Pega aquí el token entrante generado en Afrogato. Gestión Total lo usará cuando el PV asigne puntos o consuma un QR.</small>
              <small>Actual: <b>{{ siteForm.outbound_sync_token_preview || 'sin configurar' }}</b></small>
            </div>
            <label>
              <span>Token para sincronizar hacia el sitio</span>
              <input v-model="siteForm.outbound_sync_token" type="password" placeholder="Pegar token entrante generado en Afrogato" autocomplete="off" />
              <small class="field-hint">Solo se actualiza si escribes uno nuevo.</small>
            </label>
            <label v-if="siteForm.id" class="switch-row">
              <input v-model="siteForm.clear_outbound_sync_token" type="checkbox" />
              <span>Limpiar token de sincronización saliente</span>
            </label>
          </div>

          <div class="form-grid lower-fields">
            <label>
              <span>Centavos por punto ganado</span>
              <input v-model.number="siteForm.earn_amount_cents_per_point" type="number" min="1" step="1" />
            </label>
            <label>
              <span>Puntos por $1 canjeado</span>
              <input v-model.number="siteForm.redeem_points_per_mxn" type="number" min="1" step="1" />
            </label>
            <label>
              <span>Estatus</span>
              <select v-model="siteForm.status">
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
                <option value="revoked">Revocado</option>
              </select>
            </label>
          </div>

          <button class="gt-button primary full" :disabled="loading" @click="saveSite">Guardar sitio externo</button>

          <div v-if="plainToken" class="token-box">
            <small>Token de Gestión Total generado. Cópialo ahora y pégalo en Afrogato &gt; Configuración &gt; Lealtad &gt; Token de Gestión Total.</small>
            <code>{{ plainToken }}</code>
            <button class="gt-button soft full" @click="copyToken">Copiar token</button>
          </div>
        </section>

        <section class="gt-card sites-card">
          <h2>Sitios conectados</h2>
          <div class="sites-list">
            <button v-for="site in sites" :key="site.id" class="site-row" @click="editSite(site)">
              <div>
                <strong>{{ site.name }}</strong>
                <small>{{ site.code }} · {{ site.domain }}</small>
                <small>Token GT: {{ site.token_preview || 'sin configurar' }} · Token sitio: {{ site.outbound_sync_token_preview || 'sin configurar' }}</small>
              </div>
              <span :class="['status', site.status]">{{ site.status }}</span>
            </button>
            <div v-if="!sites.length" class="empty-box">Aún no tienes sitios externos conectados.</div>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'LoyaltySettingsView',
  data() {
    return {
      loading: false,
      message: '',
      messageType: 'success',
      settings: {},
      sites: [],
      plainToken: '',
      automationToken: '',
      automationLoading: false,
      siteForm: this.emptyForm(),
    };
  },
  mounted() {
    this.loadAll();
  },
  computed: {
    automationEndpoint() { return `${window.location.origin}/api/automation/loyalty/outbox`; },
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: 'Afrogato App',
        code: 'afrogato_app',
        domain: 'afrogato.app',
        api_base_url: 'https://afrogato.app',
        ticket_claim_base_url: '',
        ticket_claim_base_url_override: '',
        token_preview: '',
        inbound_token: '',
        generate_inbound_token: true,
        outbound_sync_token: '',
        outbound_sync_token_preview: '',
        clear_outbound_sync_token: false,
        earn_amount_cents_per_point: 1000,
        redeem_points_per_mxn: 1,
        status: 'active',
      };
    },
    async loadAll() {
      this.loading = true;
      try {
        const [settings, sites] = await Promise.all([
          api.get('/api/pos/loyalty/settings'),
          api.get('/api/pos/loyalty/sites'),
        ]);
        this.settings = settings.data.settings || {};
        this.sites = sites.data.sites || [];
      } catch (error) {
        this.show('No se pudo cargar lealtad. Revisa que hayas ejecutado los SQL 001 y 002.', 'error');
      } finally {
        this.loading = false;
      }
    },
    editSite(site) {
      this.plainToken = '';
      this.siteForm = {
        ...this.emptyForm(),
        ...site,
        inbound_token: '',
        generate_inbound_token: false,
        outbound_sync_token: '',
        clear_outbound_sync_token: false,
      };
    },
    resetForm() {
      this.plainToken = '';
      this.siteForm = this.emptyForm();
    },
    async saveSite() {
      this.loading = true;
      this.plainToken = '';
      try {
        const payload = {
          ...this.siteForm,
          ticket_claim_base_url: this.siteForm.ticket_claim_base_url_override || '',
          rotate_token: !!this.siteForm.generate_inbound_token,
        };
        delete payload.ticket_claim_base_url_override;
        const request = this.siteForm.id
          ? api.put(`/api/pos/loyalty/sites/${this.siteForm.id}`, payload)
          : api.post('/api/pos/loyalty/sites', payload);
        const { data } = await request;
        this.plainToken = data.plain_token || '';
        this.show(this.plainToken ? 'Sitio guardado. Copia el token de Gestión Total y pégalo en Afrogato.' : 'Sitio guardado.', 'success');
        await this.loadAll();
        if (data.site) this.editSite(data.site);
      } catch (error) {
        this.show(error.response?.data?.error || 'No se pudo guardar el sitio.', 'error');
      } finally {
        this.loading = false;
      }
    },
    async copyToken() {
      try {
        await navigator.clipboard.writeText(this.plainToken);
        this.show('Token copiado. Ahora pégalo en Afrogato como Token de Gestión Total.', 'success');
      } catch (error) {
        this.show('No se pudo copiar automáticamente. Selecciona el token y cópialo manualmente.', 'error');
      }
    },
    async createAutomationToken() {
      this.automationLoading = true;
      this.automationToken = '';
      try {
        const { data } = await api.post('/api/automation/tokens', {
          name: 'Cron de reintento de lealtad',
          scope: 'loyalty.outbox.dispatch',
          expires_in_days: 365,
        });
        this.automationToken = data.automation_token?.token || '';
        this.show('Token de automatización generado. Guárdalo antes de salir de esta pantalla.', 'success');
      } catch (error) {
        this.show(error.response?.data?.error || 'No se pudo generar el token de automatización.', 'error');
      } finally {
        this.automationLoading = false;
      }
    },
    async copyAutomationToken() {
      try {
        await navigator.clipboard.writeText(this.automationToken);
        this.show('Token de automatización copiado.', 'success');
      } catch (_error) {
        this.show('Selecciona el token y cópialo manualmente.', 'error');
      }
    },
    async copyAutomationCommand() {
      const command = `*/2 * * * * /usr/bin/curl --fail --silent --show-error --max-time 55 --request POST '${this.automationEndpoint}' --header 'Authorization: Bearer ${this.automationToken}' --header 'Content-Type: application/json' --data '{"limit":100}'`;
      try {
        await navigator.clipboard.writeText(command);
        this.show('Comando de cPanel copiado.', 'success');
      } catch (_error) {
        this.show('No se pudo copiar el comando automáticamente.', 'error');
      }
    },
    show(text, type = 'success') {
      this.message = text;
      this.messageType = type;
      window.clearTimeout(this._msgTimer);
      this._msgTimer = window.setTimeout(() => { this.message = ''; }, 7000);
    },
  },
};
</script>

<style lang="scss" scoped>
.hero { padding: 2rem; display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
.hero-actions { display: flex; gap: .75rem; flex-wrap: wrap; }
h1 { margin: .75rem 0 .35rem; font-size: clamp(2rem, 4vw, 3.4rem); }
.cards { margin-top: 1.2rem; }
.stat { padding: 1.35rem; }
.stat span { color: var(--gt-muted); font-weight: 800; }
.stat strong { display: block; margin-top: .6rem; font-size: 1.45rem; }
.stat small { color: var(--gt-muted); }
.panel-grid { display: grid; grid-template-columns: 1.15fr .85fr; gap: 1rem; margin-top: 1rem; align-items: start; }
.automation-card { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 1rem; align-items: center; margin-top: 1rem; padding: 1.35rem; }
.automation-card h2 { margin-top: .65rem; }.automation-card p { margin: .25rem 0 0; max-width: 54rem; }.automation-secret { grid-column: 1 / -1; display: grid; gap: .55rem; border: 1px solid rgba(16,185,129,.24); border-radius: 18px; padding: 1rem; background: rgba(16,185,129,.08); }.automation-secret code { display: block; overflow-wrap: anywhere; border-radius: 12px; padding: .75rem; background: #fff; }.automation-secret > div { display: flex; gap: .55rem; flex-wrap: wrap; }.automation-secret small { color: #17614f; font-weight: 800; }
.form-card, .sites-card { padding: 1.4rem; }
.section-title-row { display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start; margin-bottom: 1rem; }
h2 { margin: 0 0 .25rem; }
.map-box { display: grid; gap: .35rem; border: 1px solid rgba(59,130,246,.18); background: rgba(59,130,246,.08); border-radius: 18px; padding: 1rem; margin-bottom: 1rem; color: #1e3a8a; }
.map-box strong { font-size: .92rem; }
.map-box span { font-size: .82rem; line-height: 1.45; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .85rem; }
.lower-fields { margin-top: .95rem; }
label span { display: block; color: var(--gt-muted); font-size: .82rem; font-weight: 900; margin-bottom: .35rem; }
.field-hint { display: block; margin-top: .35rem; color: var(--gt-muted); font-size: .74rem; font-weight: 800; }
input, select { width: 100%; border: 1px solid var(--gt-border); border-radius: 16px; padding: .85rem 1rem; background: rgba(255,255,255,.76); outline: none; }
.token-section { display: grid; gap: .85rem; border: 1px solid var(--gt-border); border-radius: 22px; padding: 1rem; margin-top: 1rem; background: rgba(255,255,255,.45); }
.token-section.inbound-token { border-color: rgba(16,185,129,.22); background: rgba(16,185,129,.08); }
.token-section.outbound-token { border-color: rgba(245,158,11,.24); background: rgba(245,158,11,.08); }
.token-copy p { margin: 0 0 .25rem; font-size: 1rem; font-weight: 950; }
.token-copy small { display: block; color: var(--gt-muted); line-height: 1.45; }
.switch-row { display: flex; align-items: center; gap: .6rem; border: 1px dashed rgba(0,0,0,.08); border-radius: 14px; padding: .75rem; }
.switch-row input { width: auto; }
.switch-row span { margin: 0; color: inherit; }
.full { width: 100%; margin-top: 1rem; }
.token-box { margin-top: 1rem; border: 1px solid rgba(16,185,129,.28); background: rgba(16,185,129,.1); border-radius: 18px; padding: 1rem; }
.token-box small { display: block; color: #065f46; font-weight: 900; margin-bottom: .5rem; }
.token-box code { display: block; word-break: break-all; background: rgba(255,255,255,.8); padding: .8rem; border-radius: 12px; }
.sites-list { display: grid; gap: .75rem; }
.site-row { border: 1px solid var(--gt-border); background: rgba(255,255,255,.68); border-radius: 18px; padding: 1rem; display: flex; justify-content: space-between; gap: 1rem; text-align: left; cursor: pointer; }
.site-row strong { display: block; }
.site-row small { color: var(--gt-muted); display: block; margin-top: .2rem; }
.status { border-radius: 999px; padding: .35rem .7rem; height: fit-content; font-weight: 900; font-size: .75rem; }
.status.active { background: #dcfce7; color: #166534; }
.status.inactive { background: #f3f4f6; color: #374151; }
.status.revoked { background: #fee2e2; color: #991b1b; }
.empty-box { color: var(--gt-muted); border: 1px dashed var(--gt-border); border-radius: 18px; padding: 1rem; }
.alert { margin: 1rem 0; border-radius: 16px; padding: .9rem 1rem; font-weight: 900; }
.alert.success { background: #dcfce7; color: #166534; }
.alert.error { background: #fee2e2; color: #991b1b; }
@media (max-width: 900px) { .panel-grid, .form-grid, .automation-card { grid-template-columns: 1fr; } .automation-secret { grid-column: auto; }.hero { flex-direction: column; align-items: flex-start; } }
</style>
